import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Group,
  Loader,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
  rem,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useDebouncedValue } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { getAuditEvents } from "@utils/api-communication/RestApiCli";
import { parseDateOnlyOrNull } from "@utils/CommonUtil";
import {
  AUDIT_CATEGORIES,
  AUDIT_OUTCOMES,
  AuditEvent,
  AuditEventsResponse,
} from "./auditTrailConstants";

const PAGE_SIZE = 25;

const OUTCOME_BADGE_COLOR: Record<string, string> = {
  success: "teal",
  denied: "orange",
  failure: "red",
};

function formatGeo(event: AuditEvent): string {
  const geo = event.context?.geo;
  if (!geo?.country) return "—";
  return geo.city ? `${geo.city}, ${geo.country}` : geo.country;
}

function formatActor(event: AuditEvent): string {
  if (event.actor?.deviceId) return event.actor.deviceId;
  return event.actor?.userId ?? event.actor?.authMethod ?? "—";
}

function formatSubject(event: AuditEvent): string {
  if (!event.subject) return "—";
  const ids = event.subject.ids?.length ? ` ${event.subject.ids.join(", ")}` : "";
  return `${event.subject.type}${ids}`;
}

function outcomeKey(event: AuditEvent): string {
  const status = event.outcome?.status;
  return status && (AUDIT_OUTCOMES as readonly string[]).includes(status) ? status : "unknown";
}

const AuditTrail: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // Honoured by the backend only for SuperAdmins; for anyone else the read
  // stays scoped to their own organization regardless of what is in the URL.
  const orgId = searchParams.get("orgId");

  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [category, setCategory] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [actorUserId, setActorUserId] = useState("");
  const [debouncedActorUserId] = useDebouncedValue(actorUserId, 300);
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);

  const fetchEvents = useCallback(() => {
    setLoading(true);
    setError(false);
    getAuditEvents(
      {
        page,
        limit: PAGE_SIZE,
        ...(category ? { category } : {}),
        ...(outcome ? { outcome } : {}),
        ...(debouncedActorUserId.trim() ? { actorUserId: debouncedActorUserId.trim() } : {}),
        ...(from ? { from: from.toISOString() } : {}),
        ...(to ? { to: to.toISOString() } : {}),
        ...(orgId ? { orgId } : {}),
      },
      (response: { data: AuditEventsResponse }) => {
        setEvents(response.data.data);
        setTotal(response.data.total);
        setLoading(false);
      },
      () => {
        setError(true);
        setLoading(false);
      },
    );
  }, [page, category, outcome, debouncedActorUserId, from, to, orgId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const resetToFirstPage = <T,>(setter: (value: T) => void) => {
    return (value: T) => {
      setPage(1);
      setter(value);
    };
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Box p="md" maw={rem(1400)} mx="auto">
      <Stack gap="md">
        <Stack gap={rem(4)}>
          <Group gap="sm" align="center">
            <Title order={2}>{t("audit.title")}</Title>
            {orgId && (
              <Badge variant="light" size="lg">
                {orgId}
              </Badge>
            )}
          </Group>
          <Text size="sm" c="var(--oculera-text-secondary)">
            {t("audit.description")}
          </Text>
        </Stack>

        <Group gap="sm" align="flex-end" wrap="wrap">
          <Select
            label={t("audit.filterCategory")}
            data={AUDIT_CATEGORIES.map((value) => ({
              value,
              label: t(`audit.category.${value}`),
            }))}
            value={category}
            onChange={resetToFirstPage(setCategory)}
            clearable
            w={rem(170)}
          />
          <Select
            label={t("audit.filterOutcome")}
            data={AUDIT_OUTCOMES.map((value) => ({
              value,
              label: t(`audit.outcome.${value}`),
            }))}
            value={outcome}
            onChange={resetToFirstPage(setOutcome)}
            clearable
            w={rem(150)}
          />
          <TextInput
            label={t("audit.filterActor")}
            value={actorUserId}
            onChange={(e) => {
              setPage(1);
              setActorUserId(e.currentTarget.value);
            }}
            w={rem(220)}
          />
          <DateInput
            label={t("audit.filterFrom")}
            value={from}
            onChange={(v) => resetToFirstPage(setFrom)(parseDateOnlyOrNull(v))}
            clearable
            w={rem(160)}
          />
          <DateInput
            label={t("audit.filterTo")}
            value={to}
            onChange={(v) => resetToFirstPage(setTo)(parseDateOnlyOrNull(v))}
            clearable
            w={rem(160)}
          />
        </Group>

        {error ? (
          <Alert color="red">{t("audit.loadError")}</Alert>
        ) : loading ? (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        ) : events.length === 0 ? (
          <Text p="xl" ta="center" c="var(--oculera-text-secondary)">
            {t("audit.empty")}
          </Text>
        ) : (
          <Box style={{ overflowX: "auto" }}>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("audit.columnTime")}</Table.Th>
                  <Table.Th>{t("audit.columnEvent")}</Table.Th>
                  <Table.Th>{t("audit.columnOutcome")}</Table.Th>
                  <Table.Th>{t("audit.columnActor")}</Table.Th>
                  <Table.Th>{t("audit.columnSubject")}</Table.Th>
                  <Table.Th>{t("audit.columnIp")}</Table.Th>
                  <Table.Th>{t("audit.columnLocation")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {events.map((event) => (
                  <Table.Tr key={event.eventId}>
                    <Table.Td style={{ whiteSpace: "nowrap" }}>
                      {new Date(event.occurredAt).toLocaleString()}
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label={event.context?.route ?? event.eventType} withArrow>
                        <Text size="sm">{event.eventType}</Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={OUTCOME_BADGE_COLOR[event.outcome?.status ?? ""] ?? "gray"}
                        variant="light"
                      >
                        {t(`audit.outcome.${outcomeKey(event)}`)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{formatActor(event)}</Table.Td>
                    <Table.Td>{formatSubject(event)}</Table.Td>
                    <Table.Td>{event.context?.ip ?? "—"}</Table.Td>
                    <Table.Td>{formatGeo(event)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        )}

        {!error && (
          <Group justify="space-between">
            <Text size="sm" c="var(--oculera-text-secondary)">
              {t("audit.totalEvents", { count: total })}
            </Text>
            <Pagination value={page} onChange={setPage} total={totalPages} />
          </Group>
        )}
      </Stack>
    </Box>
  );
};

export default AuditTrail;
